<template>
  <div class="app" :class="{ loading: loadingRef }">
    <SiteHeader v-if="showHeaderRef"></SiteHeader>
    <SiteNotify ref="notifyRef"/>
    <div class="content" :class="{ embedded: appHostStore.isEmbedded, 'container-md': useContainerRef }">
      <router-view/>
    </div>
    <div class="footer">
      <SiteFooter v-if="showFooterRef"></SiteFooter>
    </div>
    <ErrorModal/>
  </div>
</template>

<style lang="scss" scoped>
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  .loading {
    display: none;
  }
}

.content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;  
}

.footer {
  position: relative;
  bottom: 0;
}
</style>

<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref, watch, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTokenManager } from '@/auth/TokenManager'
import { useAppHostStore } from './AppHostStore'
import { useGroupAdminNotifications } from '@/gp/GroupAdminNotifications'
import { useResearchDataNotifications } from '@/rd/ResearchDataNotifications'
import { useUserStore } from './gp/UserStore'
import { useExploreStore } from './explore/ExploreStore'
import { LoadMode } from './util/AsyncData'
import { reloadApp } from './util/AppUtil'
import SiteNotify from './site/SiteNotify.vue'
import SiteHeader from '@/site/SiteHeader.vue'
import SiteFooter from '@/site/SiteFooter.vue'
import ErrorModal from '@/util/ErrorModal.vue'
import { siteNotifyEvent } from './site/SiteNotifyEvent'

const notifyRef = ref<InstanceType<typeof SiteNotify>>()

const route = useRoute()
const router = useRouter()
const appHostStore = useAppHostStore()
const tokenManager = useTokenManager()
const gpNotify = useGroupAdminNotifications()
const rdNotify = useResearchDataNotifications()
const userStore = useUserStore()
const exploreStore = useExploreStore()

watch(() => tokenManager.current.userId, (newValue, oldValue) => {
  if (oldValue) {
    reloadApp() // reload entire app whenever switching users or signing out
  }
})

watchEffect(() => {
  if (router.currentRoute.value.query.embed == 'true') {
    appHostStore.isEmbedded = true // set the first time we see it, but don't expect it to be present on every page
  }
})

const loadingRef = ref(true)

const pagesWithoutHeader = [ '/signin/approve' ]
const showHeaderRef = computed(() => !appHostStore.isEmbedded && !pagesWithoutHeader.includes(route.path))

const pagesWithFooter = [ '/', '/contact', '/about', '/privacy', '/terms', '/learn/faq', '/learn/sync', '/learn/research', '/learn/compare', '/plans', '/support'  ]
const showFooterRef = computed(() => pagesWithFooter.includes(route.path))

const fullWidthPages = [ '/', '/home', '/start/family', '/start/dev', '/start/new-tree' ]
const useContainerRef = computed(() => !fullWidthPages.includes(route.path) && !route.path.startsWith('/games/'))

// start app-level initialization BEFORE mounting any child components (some of it is async, so may continue after mounting)
onBeforeMount(async () => {
  tokenManager.startAutoRefresh() // async (fire-and-forget)

  window.addEventListener('load', () => {
    loadingRef.value = false
  })

  gpNotify.initialize()
  rdNotify.initialize()

  exploreStore.ensureClientLocationLoaded()

  if (tokenManager.current.isValid) {
    // load self user immediately
    const selfUser = await userStore.getAsyncSelf(LoadMode.EnsureLoaded)?.whenLoadCompleted // loads profile group inline
  }
})

onMounted(() => {
  siteNotifyEvent.on((args) => notifyRef.value?.show(args))
})
</script>
